// src/components/Navbar/index.js
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { FiMenu, FiX } from "react-icons/fi";
// import { AuthContext } from "../../context/AuthContext";
import "./navbar.css";
import SearchBar from "../ChildcareDashboardNavbar/SearchBar";
import { FaChevronDown } from "react-icons/fa";
import { getTutorProfile } from "../../services/tutorRegistrationService";
import { getProfile as getPartnerProfile } from "../../services/partnerService";
import {
  LayoutDashboard,
  MessageSquare,
  BookOpen,
  Bookmark,
  User,
  RefreshCw,
  AlertCircle,
  CreditCard,
  Settings,
  LogOut,
  CalendarDays,
  School,
} from "lucide-react";

const NOTIF_ICON = {
  enrollment_confirmed: "🎓",
  enrollment_cancelled: "❌",
  refund_approved: "✅",
  refund_rejected: "💸",
  new_assignment: "📝",
  new_behavior_report: "📋",
  new_incident: "⚠️",
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return `${mins}m ago`;
};

const Navbar = () => {
  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const bellPanelRef = useRef(null);
  const socketRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const audioRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isActive = (path) =>
    path === "/" ? pathname === "/" : pathname.startsWith(path);

  // Get display name from user object
  const fullName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    [user?.fname, user?.lname].filter(Boolean).join(" ");
  const displayName = fullName || user?.name || user?.fullName || "User";
  const isLoggedIn = !!user;

  // Determine role and dashboard URL
  const storedRole = localStorage.getItem("role");
  const userRole =
    storedRole ||
    user?.role ||
    (user?.user_type?.startsWith("childcare") ? "childcare" : null) ||
    "parent";

  const getDashboardLink = () => {
    if (userRole === "partner") return "/partner/home";
    if (userRole === "tutor")   return "/tutor/dashboard";
    if (userRole === "childcare" || user?.user_type?.startsWith("childcare"))
      return "/childcare";
    return "/parent/dashboard";
  };
  const dashboardLink = getDashboardLink();
  const isParent = userRole === "parent";

  useEffect(() => {
    let cancelled = false;

    const loadUser = () => {
      // 1. Parent stores a full user object
      const stored = localStorage.getItem("user");
      if (stored) {
        setUser(JSON.parse(stored));
        return;
      }
      // 2. Partner / tutor store only a JWT — decode it to build a user object
      const partnerToken = localStorage.getItem("partnerToken") || sessionStorage.getItem("partnerToken");
      const tutorToken   = localStorage.getItem("tutorToken") || sessionStorage.getItem("tutorToken");
      const roleToken    = partnerToken || tutorToken;
      if (roleToken) {
        try {
          const decoded = jwtDecode(roleToken);
          const role = partnerToken ? "partner" : "tutor";
          setUser({
            id:         decoded.id,
            first_name: decoded.fname,
            last_name:  decoded.lname,
            name: decoded.fname || decoded.name || decoded.email || "User",
            email: decoded.email,
            role,
          });
          // The JWT only carries the first name — fetch the profile for the
          // last name so the navbar can show the full name.
          if (role === "tutor" && decoded.id) {
            getTutorProfile(decoded.id)
              .then((res) => {
                const lname = res.data?.data?.lname;
                if (!cancelled && lname) {
                  setUser((prev) => (prev ? { ...prev, last_name: lname } : prev));
                }
              })
              .catch(() => {});
          }
          if (role === "partner") {
            getPartnerProfile()
              .then((res) => {
                const lname = res.data?.data?.lname;
                if (!cancelled && lname) {
                  setUser((prev) => (prev ? { ...prev, last_name: lname } : prev));
                }
              })
              .catch(() => {});
          }
          return;
        } catch (_) {}
      }
      setUser(null);
    };

    loadUser();
    window.addEventListener("userChanged", loadUser);
    return () => {
      cancelled = true;
      window.removeEventListener("userChanged", loadUser);
    };
  }, []);

  // ✅ Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".user-wrapper")) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) return;

    // Fetch existing notifications
    fetch(`${process.env.REACT_APP_BACKEND_URL}/parents/notifications`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setNotifications(data.notifications);
          setUnreadCount(data.unreadCount);
        }
      })
      .catch(() => {});

    // Connect socket — loaded on demand so anonymous visitors never pull in
    // socket.io-client with the main bundle.
    let cancelled = false;
    import("socket.io-client").then(({ io }) => {
      if (cancelled) return;

      const socket = io(process.env.REACT_APP_SOCKET_URL, {
        auth: { token },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });

      socket.on("notification:new", (notif) => {
        setNotifications((prev) => [notif, ...prev]);
        setUnreadCount((prev) => prev + 1);
      });

      socketRef.current = socket;
    });

    return () => {
      cancelled = true;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user]);

  // Play sound when new notification arrives — the audio element is created
  // lazily on first use instead of on mount, so anonymous/logged-out visitors
  // never trigger the 300+ KB download.
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      if (!audioRef.current) {
        audioRef.current = new Audio("/sounds/notification.wav");
      }
      audioRef.current.play().catch(() => {});
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  // Close bell panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (bellPanelRef.current && !bellPanelRef.current.contains(e.target)) {
        setBellOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markAllRead = async () => {
    const token = localStorage.getItem("token");
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/parents/notifications/mark-read`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch (_) {}
  };

  const markOneRead = async (id) => {
    const token = localStorage.getItem("token");
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/parents/notifications/${id}/read`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (_) {}
  };

  const handleLogout = () => {
    ["user", "token", "role", "partnerToken", "tutorToken", "adminToken", "facilityId"].forEach(
      (k) => localStorage.removeItem(k)
    );
    // partnerToken is stored in sessionStorage instead of localStorage when
    // "remember me" was off at login (see PartnerContext.login) — clearing
    // only localStorage left it behind, so the next page load (triggered by
    // window.location.href below) immediately logged the partner back in.
    sessionStorage.removeItem("partnerToken");
    setUser(null);
    window.location.href = isParent ? "/Parentlogin" : "/";
  };

  // ✅ Toggle mobile menu function
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // ✅ Close mobile menu function
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div>
      {/* ===== DESKTOP NAVBAR ===== */}
      <header className="navbar-wrapper web-only">
        <nav className="navbar">
          {/* Logo */}
          <div className="nav-left">
            <Link to="/">
              <img
                src="/assets/images/newlogo.png"
                alt="Kooulu Logo"
                className="main-logo"
                width="1920"
                height="496"
              />
            </Link>
          </div>

          {/* Links */}
          <ul className="nav-links">
            <li><a href="/aboutus" className={isActive("/aboutus")                 ? "active" : ""}>About Us</a></li>
            <li className="nav-dropdown">
              <a href="/activities" className={isActive("/activities") ? "active" : ""}>Activities</a>
              <div className="nav-dropdown-menu">
                <ul className="nav-dropdown-menu-list">
                  <li>
                    <a href="/events" className={isActive("/events") ? "active" : ""}>
                      <span className="nav-dropdown-item-icon"><CalendarDays size={18} /></span>
                      <span className="nav-dropdown-item-text">
                        <span className="nav-dropdown-item-title">Events</span>
                        <span className="nav-dropdown-item-desc">Local events & meetups</span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a href="/schools" className={isActive("/schools") ? "active" : ""}>
                      <span className="nav-dropdown-item-icon"><School size={18} /></span>
                      <span className="nav-dropdown-item-text">
                        <span className="nav-dropdown-item-title">Schools</span>
                        <span className="nav-dropdown-item-desc">Find schools near you</span>
                      </span>
                    </a>
                  </li>
                </ul>
              </div>
            </li>
            <li><a href="/childcare-facility-listing" className={isActive("/childcare-facility-listing") ? "active" : ""}>Childcare Facilities</a></li>
            <li className="nav-blog-news">
              <a href="/blogs"   className={isActive("/blogs")   ? "active" : ""}>Blogs</a>
              {" / "}
              <a href="/rssfeed" className={isActive("/rssfeed") ? "active" : ""}>News</a>
            </li>
            <li><a href="/community-discussion" className={isActive("/community-discussion") ? "active" : ""}>Community</a></li>
            <li><a href="/contactus" className={isActive("/contactus") ? "active" : ""}>Contact Us</a></li>
          </ul>

          {/* Right Section */}
          <div className="nav-buttons">
            <div className="nav-search-wrapper">
              <SearchBar iconOnly />
            </div>

            {/* Bell Icon — only shown for parent users */}
            {user && isParent && (
              <div ref={bellPanelRef} style={{ position: "relative" }}>
                <button
                  onClick={() => setBellOpen((o) => !o)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    position: "relative",
                    padding: "8px",
                    borderRadius: "50%",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(255,255,255,0.2)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "none")
                  }
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#333"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {unreadCount > 0 && (
                    <span
                      style={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        background: "#ef4444",
                        color: "#fff",
                        fontSize: "10px",
                        fontWeight: "700",
                        borderRadius: "50%",
                        minWidth: "18px",
                        height: "18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1,
                        padding: "0 3px",
                      }}
                    >
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>

                {bellOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 10px)",
                      right: 0,
                      width: "360px",
                      maxHeight: "480px",
                      background: "#fff",
                      borderRadius: "14px",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                      zIndex: 9999,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        padding: "16px 20px",
                        borderBottom: "1px solid #f0f0f0",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: "700",
                            fontSize: "16px",
                            color: "#111",
                          }}
                        >
                          Notifications
                        </span>
                        {unreadCount > 0 && (
                          <span
                            style={{
                              background: "#ef3e24",
                              color: "#fff",
                              borderRadius: "20px",
                              padding: "2px 8px",
                              fontSize: "11px",
                              fontWeight: "600",
                            }}
                          >
                            {unreadCount} new
                          </span>
                        )}
                      </div>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllRead}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#ef3e24",
                            fontSize: "13px",
                            fontWeight: "600",
                          }}
                        >
                          Mark all read
                        </button>
                      )}
                    </div>

                    {/* List */}
                    <div style={{ overflowY: "auto", flex: 1 }}>
                      {notifications.length === 0 ? (
                        <div
                          style={{
                            padding: "40px 20px",
                            textAlign: "center",
                            color: "#999",
                            fontSize: "14px",
                          }}
                        >
                          <div
                            style={{ fontSize: "32px", marginBottom: "8px" }}
                          >
                            🔔
                          </div>
                          No notifications yet
                        </div>
                      ) : (
                        notifications.map((n) => (
                          <div
                            key={n.id}
                            onClick={() => {
                              if (!n.is_read) markOneRead(n.id);
                              if (n.redirect_url) {
                                navigate(n.redirect_url);
                                setBellOpen(false);
                              }
                            }}
                            style={{
                              padding: "14px 20px",
                              borderBottom: "1px solid #f9f9f9",
                              display: "flex",
                              gap: "12px",
                              alignItems: "flex-start",
                              cursor: n.redirect_url ? "pointer" : "default",
                              background: n.is_read ? "#fff" : "#fff8f5",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => {
                              if (!n.is_read)
                                e.currentTarget.style.background = "#fff3ee";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = n.is_read
                                ? "#fff"
                                : "#fff8f5";
                            }}
                          >
                            <div
                              style={{
                                width: "38px",
                                height: "38px",
                                borderRadius: "50%",
                                background: "#fef3e2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "18px",
                                flexShrink: 0,
                              }}
                            >
                              {NOTIF_ICON[n.type] || "🔔"}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: n.is_read ? "500" : "700",
                                  fontSize: "13px",
                                  color: "#111",
                                  marginBottom: "2px",
                                }}
                              >
                                {n.title}
                                {!n.is_read && (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: "6px",
                                      height: "6px",
                                      borderRadius: "50%",
                                      background: "#ef3e24",
                                      marginLeft: "6px",
                                      verticalAlign: "middle",
                                    }}
                                  />
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {n.message}
                              </div>
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#aaa",
                                  marginTop: "4px",
                                }}
                              >
                                {timeAgo(n.created_at)}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {user ? (
              <div className="user-wrapper" style={{ position: "relative" }}>
                {/* USER PROFILE */}
                <div
                  className="user-profile"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                >
                  {user.profilePic ? (
                    <img
                      src={user.profilePic}
                      alt={displayName}
                      className="profile-pic"
                    />
                  ) : (
                    <div className="profile-placeholder">
                      {displayName?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="user-name">{displayName}</span>
                  <FaChevronDown
                    className="dropdown-icon"
                    style={{ cursor: "pointer" }}
                  />
                </div>

                {/* DROPDOWN MENU */}
                {dropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "46px",
                      right: "-22%",
                      width: "240px",
                      background: "#fff",
                      borderRadius: "12px",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      padding: "10px 0",
                      zIndex: 9999,
                    }}
                  >
                    {/* Dashboard — always shown, destination is role-aware */}
                    <Link
                      to={dashboardLink}
                      onClick={() => setDropdownOpen(false)}
                      style={{
                        display: "flex", alignItems: "center", gap: "12px",
                        padding: "10px 18px", textDecoration: "none",
                        color: "#333", fontSize: "14px",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span style={{ width: "20px" }}><LayoutDashboard size={18} /></span>
                      Dashboard
                    </Link>

                    {/* Parent-only items */}
                    {isParent && [
                      { label: "Messages",        link: "/parent/messages",         icon: <MessageSquare size={18} /> },
                      { label: "My Enrollments",  link: "/parent/browse-classes",   icon: <BookOpen size={18} /> },
                      { label: "Saved Activities",link: "/parent/saved-activities", icon: <Bookmark size={18} /> },
                      { label: "Parent Profile",  link: "/parent/profile",          icon: <User size={18} /> },
                      { label: "Refund Request",  link: "/parent/refund-request",   icon: <RefreshCw size={18} /> },
                      { label: "Report Issue",    link: "/parent/report-issue",     icon: <AlertCircle size={18} /> },
                      { label: "Payments",        link: "/parent/payments",         icon: <CreditCard size={18} /> },
                      { label: "Settings",        link: "/parent/setting",          icon: <Settings size={18} /> },
                    ].map((item, index) => (
                      <Link
                        key={index}
                        to={item.link}
                        onClick={() => setDropdownOpen(false)}
                        style={{
                          display: "flex", alignItems: "center", gap: "12px",
                          padding: "10px 18px", textDecoration: "none",
                          color: "#333", fontSize: "14px",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <span style={{ width: "20px" }}>{item.icon}</span>
                        {item.label}
                      </Link>
                    ))}

                    {/* Divider */}
                    <div style={{ height: "1px", background: "#eee", margin: "8px 0" }} />

                    {/* Logout */}
                    <div
                      onClick={handleLogout}
                      style={{
                        display: "flex", alignItems: "center", gap: "12px",
                        padding: "10px 18px", color: "red", cursor: "pointer",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <LogOut size={18} /> Logout
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login-options">
                  <button className="partner-login">Partner Login</button>
                </Link>
                <Link to="/Parentlogin">
                  <button className="user-login">Parent Login</button>
                </Link>
              </>
            )}
          </div>
        </nav>
      </header>

      {/* ===== MOBILE NAVBAR ===== */}
      <header className="navbar-wrapper-mobile mobile-only">
        <nav className="navbar-mobile">
          <div className="mobile-nav">
            <div className="nav-left">
              <Link to="/">
                <img
                  src="/assets/images/newlogo.png"
                  alt="Kooulu Logo"
                  className="main-logo-mobile"
                  width="1920"
                  height="496"
                />
              </Link>
            </div>
            <div className="nav-right">
              <button
                className="hamburger-btn"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMobileMenu();
                }}
              >
                {isMobileMenuOpen ? <FiX size={26} /> : <FiMenu size={26} />}
              </button>
            </div>
          </div>

          {/* Mobile Dropdown Menu */}
          {isMobileMenuOpen && (
            <div className="mobile-dropdown-menu">
              <ul className="mobile-nav-links">
                <li>
                  <a href="/" onClick={closeMobileMenu}>
                    Home
                  </a>
                </li>
                <li>
                  <a href="/aboutus" onClick={closeMobileMenu}>
                    About us
                  </a>
                </li>
                <li>
                  <a href="/activities" onClick={closeMobileMenu}>
                    Activities
                  </a>
                </li>
                <li>
                  <a href="/events" onClick={closeMobileMenu}>
                    Events
                  </a>
                </li>
                <li>
                  <a href="/schools" onClick={closeMobileMenu}>
                    Schools
                  </a>
                </li>
                <li>
                  <a href="/childcare-facility-listing" onClick={closeMobileMenu}>
                    Childcare Facilities
                  </a>
                </li>
                <li>
                  <a href="/blogs" onClick={closeMobileMenu}>
                    Blogs
                  </a>
                </li>
                <li>
                  <a href="/rssfeed" onClick={closeMobileMenu}>
                    News
                  </a>
                </li>
                <li>
                  <a href="/community-discussion" onClick={closeMobileMenu}>
                    Community
                  </a>
                </li>
                <li>
                  <a href="/contactus" onClick={closeMobileMenu}>
                    Contact us
                  </a>
                </li>
              </ul>
              <div className="mobile-buttons">
                {isLoggedIn ? (
                  <>
                    <div className="mobile-user-info">
                      {user?.profilePic || user?.profile_image ? (
                        <img
                          src={user?.profilePic || user?.profile_image}
                          alt={displayName}
                          className="mobile-profile-pic"
                        />
                      ) : (
                        <div className="mobile-profile-placeholder">
                          {displayName?.charAt(0)?.toUpperCase() || "U"}
                        </div>
                      )}
                      <span className="mobile-user-name">{displayName}</span>
                    </div>
                    <Link
                      to="/parent/messages"
                      onClick={closeMobileMenu}
                      className="mobile-logout-btn"
                      style={{
                        textAlign: "center",
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <MessageSquare size={16} /> Messages
                    </Link>

                    <ul className="mobile-nav-links mobile-dashboard-links">
                      {/* Dashboard — role-aware */}
                      <li>
                        <Link to={dashboardLink} onClick={closeMobileMenu}>
                          <span style={{ marginRight: "8px" }}><LayoutDashboard size={16} /></span>
                          Dashboard
                        </Link>
                      </li>
                      {/* Parent-only items */}
                      {isParent && [
                        { label: "My Enrollments",   link: "/parent/browse-classes",   icon: <BookOpen size={16} /> },
                        { label: "Saved Activities",  link: "/parent/saved-activities", icon: <Bookmark size={16} /> },
                        { label: "Parent Profile",    link: "/parent/profile",          icon: <User size={16} /> },
                        { label: "Refund Request",    link: "/parent/refund-request",   icon: <RefreshCw size={16} /> },
                        { label: "Report Issue",      link: "/parent/report-issue",     icon: <AlertCircle size={16} /> },
                        { label: "Payments",          link: "/parent/payments",         icon: <CreditCard size={16} /> },
                        { label: "Settings",          link: "/parent/setting",          icon: <Settings size={16} /> },
                      ].map((item, index) => (
                        <li key={index}>
                          <Link to={item.link} onClick={closeMobileMenu}>
                            <span style={{ marginRight: "8px" }}>{item.icon}</span>
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={handleLogout}
                      className="mobile-logout-btn"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login-options" onClick={closeMobileMenu}>
                      <button className="mobile-partner-login">
                        Partner Login
                      </button>
                    </Link>
                    <Link to="/Parentlogin" onClick={closeMobileMenu}>
                      <button className="mobile-user-login">
                        Parent Login
                      </button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>
    </div>
  );
};

export default Navbar;
